// @ts-strict-ignore
import React from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  MenuItem,
} from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import MoreButtonMenu from 'components/MoreButtonMenu'
import { IUploadedVideo } from 'hooks/video'
import { useSnackbar } from 'hooks/redux'
import { graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay'
import { SuggestedQuestions_Query } from '__generated__/SuggestedQuestions_Query.graphql'
import { useRouter } from 'next/router'
import useStyles from './styles'
import SuggestedQuestionsList from '../SuggestedQuestionsList'

export const SuggestedQuestionsQuery = graphql`
  query SuggestedQuestions_Query {
    currentUser {
      suggestedQuestions {
        ...SuggestedQuestionsList_Questions
      }
      ...SuggestedQuestionsList_User
    }
  }
`

export interface ISuggestedQuestionsProps {
  onVideoSaved?: (video: IUploadedVideo) => void
  preloadedQuery: PreloadedQuery<SuggestedQuestions_Query>
  flatCard?: boolean
  showSubmenu?: boolean
}

export default function SuggestedQuestions({ onVideoSaved, preloadedQuery, flatCard, showSubmenu = true }: ISuggestedQuestionsProps) {
  const { classes } = useStyles()
  const { showSnackbarMessage } = useSnackbar()

  const [query, loadQuery] = useQueryLoader(SuggestedQuestionsQuery, preloadedQuery)
  const data = usePreloadedQuery<SuggestedQuestions_Query>(SuggestedQuestionsQuery, query)
  const questions = data?.currentUser?.suggestedQuestions
  const router = useRouter()

  const onAnswerSaved = () => {
    showSnackbarMessage('Answer Saved', { level: 'success' })
    loadQuery({}, { fetchPolicy: 'store-and-network' })
  }

  if (!questions) {
    return null
  }

  return (
    <Card
      // this component is used by both the member dashboard and the freelancer profile page, so it needs distinct styling modes
      // TODO: unify styling and clean this up
      className={flatCard ? undefined : classes.container}
      raised={!flatCard}
      variant={flatCard ? 'outlined' : 'elevation'}
      data-cy="dashboard-suggested-questions"
    >
      <CardHeader
        title="Suggested Questions"
        subheader={`${questions.length} available`}
        action={showSubmenu && (
          <MoreButtonMenu>
            <MenuItem onClick={() => router.push('/profile#1')}>
              Manage My Answers
            </MenuItem>
          </MoreButtonMenu>
        )}
      />

      {questions.length > 0 && (
        <CardContent>
          <Typography variant="body1">
            Improve your profile by answering the most popular expert questions.
            Profiles with more answers get more views and appear first to clients in job postings
          </Typography>
        </CardContent>
      )}

      <CardContent>
        <SuggestedQuestionsList
          userFragmentRef={data?.currentUser}
          questionsFragmentRef={questions}
          onAnswerSaved={onAnswerSaved}
          onVideoSaved={onVideoSaved}
        />

        {!questions?.length && (
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle /></ListItemIcon>
              <ListItemText
                primary="All set!"
                secondary="We don't have any more questions for you at this time. Good job!"
              />
            </ListItem>
          </List>
        )}
      </CardContent>
    </Card>
  )
}
